import type { Feature } from 'ol';
import type { ReadOptions } from 'ol/format/Feature';
import type { Options as SourceOptions } from 'ol/source/Vector';
import type { Projection as OLProjection, ProjectionLike } from 'ol/proj';

import type { ConfigBaseClass, TypeLayerEntryShell } from '@/api/config/validation-classes/config-base-class';
import { AbstractGeoViewVector } from '@/geo/layer/geoview-layers/vector/abstract-geoview-vector';
import { EsriFeatureLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/esri-feature-layer-entry-config';
import type { VectorLayerEntryConfig } from '@/api/config/validation-classes/vector-layer-entry-config';
import type {
  TypeGeoviewLayerConfig,
  TypeMetadataEsriDynamic,
  TypeMetadataEsriDynamicLayer,
  TypeMetadataEsriFeature,
} from '@/api/types/layer-schema-types';
import { CONST_LAYER_TYPES } from '@/api/types/layer-schema-types';

import { EsriUtilities } from '@/geo/layer/geoview-layers/esri-layer-common';
import {
  LayerEntryConfigLayerIdEsriMustBeNumberError,
  LayerFeatureParsingError,
  LayerTooManyEsriFeatures,
} from '@/core/exceptions/layer-exceptions';
import { AbstractGeoViewRaster } from '@/geo/layer/geoview-layers/raster/abstract-geoview-raster';
import { GVEsriFeature } from '@/geo/layer/gv-layers/vector/gv-esri-feature';
import { Fetch } from '@/core/utils/fetch-helper';
import { formatError, NetworkError, RequestTimeoutError, ResponseContentError, ResponseError } from '@/core/exceptions/core-exceptions';
import { GeoUtilities, type FetchWithProxyResult, type SourceFeaturesInfo } from '@/geo/utils/utilities';
import { AsyncSemaphore } from '@/core/utils/async-semaphore';
import { delay } from '@/core/utils/utilities';
import { logger } from '@/core/utils/logger';
import type { DisplayDateMode } from '@/api/types/map-schema-types';

export interface TypeEsriFeatureLayerConfig extends TypeGeoviewLayerConfig {
  geoviewLayerType: typeof CONST_LAYER_TYPES.ESRI_FEATURE;
  listOfLayerEntryConfig: EsriFeatureLayerEntryConfig[];
}

/**
 * A class to add an EsriFeature layer.
 */
export class EsriFeature extends AbstractGeoViewVector {
  /** Maximum number of paged chunk requests fetched concurrently (mirrors the ESRI worker concurrency cap). */
  static readonly #MAX_CONCURRENT_CHUNK_REQUESTS = Math.min(10, (navigator.hardwareConcurrency || 4) * 2);

  /** Maximum number of attempts (initial try plus retries) for a single chunk request. */
  static readonly #MAX_CHUNK_FETCH_ATTEMPTS = 4;

  /** Base backoff delay in milliseconds between chunk retries, doubled on each attempt. */
  static readonly #CHUNK_RETRY_BASE_DELAY_MS = 1000;

  /**
   * Constructs an EsriFeature Layer configuration processor.
   *
   * @param layerConfig - The layer configuration
   */
  // The constructor is not useless, it narrows down the accepted parameter type.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(layerConfig: TypeEsriFeatureLayerConfig) {
    super(layerConfig);
  }

  // #region OVERRIDES

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed layer configuration specific to this layer
   */
  override getGeoviewLayerConfig(): TypeEsriFeatureLayerConfig {
    return super.getGeoviewLayerConfig() as TypeEsriFeatureLayerConfig;
  }

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * Sometimes, the layer processing uses metadata coming from MapServer/?f=json (TypeMetadataEsriDynamic)
   * and sometimes from FeatureServer/?f=json (TypeMetadataEsriFeature) which is the reason for the double types.
   *
   * @returns The strongly-typed layer metadata specific to this layer
   */
  override getMetadata(): TypeMetadataEsriDynamic | TypeMetadataEsriFeature | undefined {
    return super.getMetadata() as TypeMetadataEsriDynamic | TypeMetadataEsriFeature | undefined;
  }

  /**
   * Overrides the way the metadata is fetched.
   *
   * Returns TypeMetadataEsriDynamic | TypeMetadataEsriDynamicLayer | TypeMetadataEsriFeature because sometimes
   * the url is MapServer/?f=json, sometimes MapServer/{layerId}?f=json and sometimes FeatureServer/?f=json
   * which all return different payloads.
   *
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves with the fetched metadata and proxy information
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   */
  protected override onFetchServiceMetadata(abortSignal?: AbortSignal): Promise<FetchWithProxyResult<unknown>> {
    // Redirect
    return this.helperFetchServiceMetadataWithFJson(abortSignal);
  }

  /**
   * Overrides the way a geoview layer config initializes its layer entries.
   *
   * @returns A promise that resolves once the layer entries have been initialized
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   */
  protected override async onInitLayerEntries(): Promise<TypeGeoviewLayerConfig> {
    // Calls fetchServiceMetadata which delegates to this class's overridden onFetchServiceMetadata (may use a proxy fallback and store the proxyUrl on the instance)
    // In this init context we fetch either via /MapServer/{layerId} or /FeatureServer url endpoints
    const fetchResult = await this.fetchServiceMetadata<TypeMetadataEsriDynamic | TypeMetadataEsriDynamicLayer | TypeMetadataEsriFeature>();

    // If metadata was fetched successfully
    const entries = [];
    let finalUrl = this.getMetadataAccessPath();
    if (fetchResult.data) {
      // Detect the service type separator (MapServer or FeatureServer)
      const url = this.getMetadataAccessPath().toLowerCase();
      const separators = ['/mapserver', '/featureserver'];
      const sep = separators.find((s) => url.lastIndexOf(s) > 0);

      if (sep) {
        const idx = url.lastIndexOf(sep);
        const afterSep = this.getMetadataAccessPath().substring(idx + sep.length);

        // A numeric layer id must follow the separator (e.g., /MapServer/0 or /FeatureServer/0)
        if (!/^\/\d+/.test(afterSep)) {
          throw new LayerEntryConfigLayerIdEsriMustBeNumberError(
            this.getGeoviewLayerId(),
            'undefined',
            this.getLayerEntryNameOrGeoviewLayerName()
          );
        }

        // Metadata is at root level when a layer id is present
        const metadataLayer = fetchResult.data as TypeMetadataEsriDynamicLayer;
        finalUrl = this.getMetadataAccessPath().substring(0, idx + sep.length);

        entries.push({
          id: metadataLayer.id,
          index: metadataLayer.id,
          layerId: metadataLayer.id,
          layerName: metadataLayer.name,
        });
      }
    }

    // Redirect
    return EsriFeature.createGeoviewLayerConfig(
      this.getGeoviewLayerId(),
      this.getGeoviewLayerName(),
      finalUrl,
      this.getGeoviewLayerConfig().isTimeAware,
      entries
    );
  }

  /**
   * This method validates recursively the configuration of the layer entries to ensure that it is a feature layer identified
   * with a numeric layerId and creates a group entry when a layer is a group.
   *
   * @param listOfLayerEntryConfig - The list of layer entries configuration to validate
   */
  protected override onValidateListOfLayerEntryConfig(listOfLayerEntryConfig: ConfigBaseClass[]): void {
    // Redirect and hook when a layer entry must be registered
    EsriUtilities.commonValidateListOfLayerEntryConfig(this, listOfLayerEntryConfig, (config) => {
      // Register the layer entry config
      this.emitLayerEntryRegisterInit({ config });
    });
  }

  /**
   * Overrides the way the layer metadata is processed.
   *
   * @param layerConfig - The layer entry configuration to process
   * @param displayDateMode - The display date mode to use for processing time dimensions in the metadata
   * @param mapProjection - Optional map projection
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves once the layer entry configuration has gotten its metadata processed
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   * @throws {LayerServiceMetadataEmptyError} When the layer metadata response is empty
   * @throws {InvalidTimeDimensionError} When the time dimension range cannot be computed
   * @throws {InvalidDateError} When the time dimension contains invalid dates
   * @throws {InvalidProjectionError} When the metadata projection is invalid
   * @throws {RequestTimeoutError} When fetching a missing projection definition exceeds the timeout duration
   * @throws {RequestAbortedError} When fetching a missing projection definition is aborted
   * @throws {ResponseError} When fetching a missing projection definition returns an unsuccessful response
   * @throws {ResponseEmptyError} When a missing projection definition response is empty
   * @throws {NetworkError} When fetching a missing projection definition encounters a network error
   */
  protected override onProcessLayerMetadata(
    layerConfig: EsriFeatureLayerEntryConfig,
    displayDateMode: DisplayDateMode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mapProjection?: OLProjection,
    abortSignal?: AbortSignal
  ): Promise<EsriFeatureLayerEntryConfig> {
    return EsriUtilities.initLayerMetadata(layerConfig, displayDateMode, abortSignal);
  }

  /**
   * Overrides the loading of the vector features for the layer by fetching EsriFeature data and converting it
   * into OpenLayers {@link Feature} feature instances.
   *
   * @param layerConfig - The configuration object for the vector layer, containing source and data access information
   * @param sourceOptions - The OpenLayers vector source options associated with the layer
   * @param readOptions - Options controlling how features are read, including the target `featureProjection`
   * @returns A promise that resolves to an array of OpenLayers features
   * @throws {LayerTooManyEsriFeatures} When the layer has too many Esri features
   */
  protected override async onCreateVectorSourceLoadFeatures(
    layerConfig: VectorLayerEntryConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sourceOptions: SourceOptions<Feature>,
    readOptions: ReadOptions
  ): Promise<SourceFeaturesInfo> {
    // Cast it to proper type
    const layerConfigEsriFeature = layerConfig as EsriFeatureLayerEntryConfig;

    // Get the url to fetch features
    const url = `${layerConfigEsriFeature.getDataAccessPathProxiedWhenNecessary(true)}${layerConfigEsriFeature.layerId}`;

    // Use the basic fetch
    const responseDataCount = await Fetch.fetchEsriJson<{ count: number }>(`${url}/query?f=json&where=1=1&returnCountOnly=true`);

    // Check if feature count is too large
    if (responseDataCount.count > AbstractGeoViewVector.MAX_ESRI_FEATURES) {
      // Throw
      throw new LayerTooManyEsriFeatures(
        layerConfigEsriFeature.layerId,
        layerConfigEsriFeature.getLayerNameCascade(),
        responseDataCount.count
      );
    }

    // Determine the maximum number of records allowed
    const maxRecords = layerConfigEsriFeature.getLayerMetadata()?.maxRecordCount;

    // Retrieve the full ESRI feature data
    const responseData = await EsriFeature.#fetchEsriFeaturesByChunk(
      `${url}/query?f=json&where=1=1&outfields=*&geometryPrecision=1&maxAllowableOffset=5`,
      responseDataCount.count,
      maxRecords
    );

    // Convert each ESRI response chunk to features and flatten the result
    let hadInvalidGeometries = false;
    let dataProjection: ProjectionLike;
    try {
      const featureResults = await Promise.all(
        responseData.map((json) => {
          return GeoUtilities.readFeaturesFromEsriJSON(json, readOptions.dataProjection, readOptions.featureProjection);
        })
      );

      const allFeatures = featureResults.flatMap((featureResult, index) => {
        // capture projection once (first valid result wins)
        if (index === 0) {
          // eslint-disable-next-line prefer-destructuring
          dataProjection = featureResult.dataProjection;
        }

        if (featureResult.hadInvalidGeometries) {
          hadInvalidGeometries = true;
        }

        return featureResult.features;
      });

      // If we had to clean geometries, emit a warning message
      if (hadInvalidGeometries) {
        this.emitMessage('warning.layer.invalidGeometry', { layerName: layerConfigEsriFeature.getLayerNameCascade() }, 'warning');
      }

      return { features: allFeatures, dataProjection };
    } catch (error: unknown) {
      throw new LayerFeatureParsingError(layerConfigEsriFeature.layerId, layerConfigEsriFeature.getLayerNameCascade(), formatError(error));
    }
  }

  /**
   * Overrides the creation of the GV Layer.
   *
   * @param layerConfig - The layer entry configuration
   * @returns The GV Layer
   */
  protected override onCreateGVLayer(layerConfig: EsriFeatureLayerEntryConfig): GVEsriFeature {
    // Create the source
    const source = this.createVectorSource(layerConfig);
    // Create the GV Layer
    const gvLayer = new GVEsriFeature(source, layerConfig);
    // Return it
    return gvLayer;
  }

  // #endregion OVERRIDES

  // #region STATIC METHODS

  /**
   * Initializes a GeoView layer configuration for a Esri Feature layer.
   *
   * This method creates a basic TypeGeoviewLayerConfig using the provided
   * ID, name, and metadata access path URL. It then initializes the layer entries by calling
   * `initGeoViewLayerEntries`, which may involve fetching metadata or sublayer info.
   *
   * @param geoviewLayerId - A unique identifier for the layer
   * @param geoviewLayerName - The display name of the layer
   * @param metadataAccessPath - The full service URL to the layer endpoint
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @returns A promise that resolves to an initialized GeoView layer configuration with layer entries
   */
  static initGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    metadataAccessPath: string,
    isTimeAware?: boolean
  ): Promise<TypeGeoviewLayerConfig> {
    // Create the Layer config
    const myLayer = new EsriFeature({ geoviewLayerId, geoviewLayerName, metadataAccessPath, isTimeAware } as TypeEsriFeatureLayerConfig);
    return myLayer.initGeoViewLayerEntries();
  }

  /**
   * Creates a configuration object for an Esri Feature layer.
   *
   * This function constructs a `TypeEsriFeatureLayerConfig` object that describes an Esri Feature layer
   * and its associated entry configurations based on the provided parameters.
   *
   * @param geoviewLayerId - A unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name of the GeoView layer
   * @param metadataAccessPath - The full service URL to the layer endpoint
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @param layerEntries - An array of layer entries objects to be included in the configuration
   * @returns The constructed configuration object for the Esri Feature layer
   */
  static createGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string | undefined,
    metadataAccessPath: string,
    isTimeAware: boolean | undefined,
    layerEntries: TypeLayerEntryShell[]
  ): TypeEsriFeatureLayerConfig {
    const geoviewLayerConfig: TypeEsriFeatureLayerConfig = {
      geoviewLayerId,
      geoviewLayerName,
      metadataAccessPath,
      geoviewLayerType: CONST_LAYER_TYPES.ESRI_FEATURE,
      isTimeAware,
      listOfLayerEntryConfig: [],
    };
    // For ESRI Feature, the numeric id is also the service layer index
    const enrichedEntries = layerEntries.map((entry) => ({
      ...entry,
      index: entry.index ?? (typeof entry.id === 'number' ? entry.id : undefined),
    }));

    geoviewLayerConfig.listOfLayerEntryConfig = enrichedEntries.map((layerEntry) => {
      // TODO: REFACTOR IMPORTANT - This spreading should be rewritten like explained in GeoJSON file. Search id : 59026aa9

      const layerEntryConfig = new EsriFeatureLayerEntryConfig({
        geoviewLayerConfig,
        layerId: `${layerEntry.index || layerEntry.id}`,
        ...(layerEntry.layerName && { layerName: `${layerEntry.layerName}` }),
      });
      return layerEntryConfig;
    });

    // Return it
    return geoviewLayerConfig;
  }

  /**
   * Processes an Esri Feature GeoviewLayerConfig and returns a promise
   * that resolves to an array of `ConfigBaseClass` layer entry configurations.
   *
   * This method:
   * 1. Creates a Geoview layer configuration using the provided parameters.
   * 2. Instantiates a layer with that configuration.
   * 3. Processes the layer configuration and returns the result.
   *
   * @param geoviewLayerId - The unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name for the GeoView layer
   * @param url - The URL of the service endpoint
   * @param layerEntries - An array of layer entry shells to include in the configuration
   * @param isTimeAware - Indicates if the layer is time aware
   * @returns A promise that resolves to an array of layer configurations
   * @throws {LayerServiceMetadataUnableToFetchError} When service metadata cannot be fetched
   * @throws {LayerServiceMetadataEmptyError} When the service metadata response is empty
   * @throws {LayerEntryConfigLayerIdNotFoundError} When a configured layer ID is not found
   * @throws {LayerDataAccessPathMandatoryError} When a layer data access path is not configured
   * @throws {LayerEntryConfigEmptyLayerGroupError} When an empty layer group prevents the root layer from being created
   * @throws {LayerEntryConfigUnableToCreateGroupLayerError} When a layer group cannot be created
   * @throws {AggregateError} When multiple layer entries fail to process
   */
  static processGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    url: string,
    layerEntries: TypeLayerEntryShell[],
    isTimeAware: boolean
  ): Promise<ConfigBaseClass[]> {
    // Create the Layer config
    const layerConfig = EsriFeature.createGeoviewLayerConfig(geoviewLayerId, geoviewLayerName, url, isTimeAware, layerEntries);

    // Create the class from geoview-layers package
    const myLayer = new EsriFeature(layerConfig);

    // Process it
    return AbstractGeoViewRaster.processConfig(myLayer);
  }

  /**
   * Fetches features from ESRI Feature services with query and feature limits.
   *
   * Chunk requests are throttled to a maximum concurrency and each chunk is retried with exponential backoff on
   * transient failures (server HTTP 5xx, ESRI embedded query error, network error, timeout). Bursting every page at
   * once makes the ArcGIS server return load-induced `500 | Error performing query operation` responses on large
   * layers; throttling and retrying let the layer load reliably on the first attempt instead of being dropped.
   *
   * @param url - The base url for the service
   * @param featureCount - The number of features in the layer
   * @param maxRecordCount - Optional max features per query from the service
   * @param featureLimit - Optional maximum number of features to fetch per query
   * @returns A promise that resolves to an array of the response text for the features
   * @throws {ResponseContentError} When a chunk keeps failing after all attempts (propagated from `fetchEsriJson()`)
   */
  // GV: featureLimit ideal amount varies with the service and with maxAllowableOffset.
  // TODO: Add options for featureLimit to config
  static #fetchEsriFeaturesByChunk(url: string, featureCount: number, maxRecordCount?: number, featureLimit = 1000): Promise<unknown[]> {
    // Update url
    const featureFetchLimit = maxRecordCount && maxRecordCount < featureLimit ? maxRecordCount : featureLimit;

    // GV: Web worker does not improve the performance of this fetching
    // Create array of url's to call, one per feature page
    const urlArray: string[] = [];
    for (let i = 0; i < featureCount; i += featureFetchLimit) {
      urlArray.push(`${url}&resultOffset=${i}&resultRecordCount=${featureFetchLimit}`);
    }

    // Cap concurrent chunk requests; firing all pages at once makes the server return transient 500s under load
    const semaphore = new AsyncSemaphore(EsriFeature.#MAX_CONCURRENT_CHUNK_REQUESTS);

    // Fetch each chunk under the concurrency cap, retrying transient failures with backoff
    const promises = urlArray.map((featureUrl) => semaphore.withLock(() => EsriFeature.#fetchEsriChunkWithRetry(featureUrl)));

    // Return the all promise
    return Promise.all(promises);
  }

  /**
   * Fetches a single ESRI feature chunk, retrying transient failures with exponential backoff.
   *
   * @param url - The chunk query url
   * @param attempt - The current attempt number (1-based)
   * @returns A promise that resolves to the chunk response
   * @throws {ResponseContentError} When the chunk keeps failing after all attempts (propagated from `fetchEsriJson()`)
   */
  static async #fetchEsriChunkWithRetry(url: string, attempt = 1): Promise<unknown> {
    try {
      return await Fetch.fetchEsriJson(url);
    } catch (error) {
      // Give up when out of attempts or when the error is not a transient server/network failure
      if (attempt >= EsriFeature.#MAX_CHUNK_FETCH_ATTEMPTS || !EsriFeature.#isTransientEsriError(error)) throw error;

      // Back off (1s, 2s, 4s, …) to let the server recover / warm its cache before retrying
      logger.logWarning(`ESRI feature chunk request failed (attempt ${attempt}), retrying...`, error);
      await delay(EsriFeature.#CHUNK_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      return EsriFeature.#fetchEsriChunkWithRetry(url, attempt + 1);
    }
  }

  /**
   * Determines whether a chunk request error is transient and worth retrying.
   *
   * Deliberate cancellations (aborts) are never retried; server content/HTTP errors, network errors and timeouts are
   * treated as transient because they are typically load-induced for large geometry queries.
   *
   * @param error - The error thrown by the chunk request
   * @returns Whether the error is transient
   */
  static #isTransientEsriError(error: unknown): boolean {
    return (
      error instanceof ResponseContentError ||
      error instanceof ResponseError ||
      error instanceof NetworkError ||
      error instanceof RequestTimeoutError
    );
  }

  // #endregion STATIC METHODS
}
